import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Space,
  theme,
  Typography,
} from "antd";
import { DepartmentForm } from "./DepartmentFormDialog.default";
import { useEffect, useState } from "react";
import {
  addCompanyDepartment,
  deleteCompanyDepartment,
  updateCompanyDepartment,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";

export interface DepartmentFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: DepartmentForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function DepartmentFormDialog(props: DepartmentFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<DepartmentForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong, try again",
  );

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue(props.initialValues);
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
            .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values]);

  function onSubmit(): void {
    const { name } = values;
    setLoading(true);
    setError(false);
    if (props.mode === "add") {
      addCompanyDepartment({
        name,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((e: any) => {
          setErrorMessage(getProblemMessage(e));
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "update") {
      updateCompanyDepartment({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        name,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((e: any) => {
          setErrorMessage(getProblemMessage(e));
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "delete") {
      deleteCompanyDepartment(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((e: any) => {
          setErrorMessage(getProblemMessage(e));
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    form.resetFields();
  }

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add";
      case "update":
        return "Save";
      case "delete":
        return "Delete";
    }
  }

  return (
    <Modal
      open={props.open}
      centered
      title={createMode() + " Department"}
      footer={null}
      maskClosable={false}
      onCancel={onCancel}
    >
      <Col className="flex flex-col overflow-hidden max-h-[512px] w-[720px] overflow-y-auto">
        {props.mode !== "delete" && (
          <Form
            form={form}
            layout={"vertical"}
            autoComplete="off"
            variant={"filled"}
            colon={false}
          >
            <Form.Item
              className="mb-2"
              name={"name"}
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Name
                </Typography.Text>
              }
              rules={[
                {
                  required: true,
                  message: "Name is required",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Form>
        )}
        {props.mode === "delete" && (
          <Typography.Paragraph>
            <Typography.Text>
              Continue deleting the{" "}
              <Typography.Text className="font-bold">
                {props.initialValues?.name}
              </Typography.Text>{" "}
              department?
            </Typography.Text>
          </Typography.Paragraph>
        )}
        {error && (
          <Alert className="mb-2" message={errorMessage} type="error" />
        )}
        {/* Actions */}
        <Space className="sticky bottom-0 flex flex-row items-center justify-end">
          <Button disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type={"primary"}
            danger={props.mode === "delete"}
            loading={loading}
            disabled={!valid && props.mode !== "delete"}
            onClick={onSubmit}
          >
            {createMode()}
          </Button>
        </Space>
      </Col>
    </Modal>
  );
}

export default DepartmentFormDialog;
